/* attachmentBytes — browser-only helpers to resolve a quote attachment to the
   base64 bytes the AI extraction sends to Claude (Step 4 Slice 3).

   Kept separate from ai.js (which is unit-tested in jsdom) because these touch
   FileReader / fetch. ai.js's buildExtractionContent consumes the {kind, b64,
   mediaType} shape these return and is the part under test. */

// Anthropic accepts only these image media types; HEIC/TIFF/BMP would 400 the
// whole request, so they're saved but not sent (F2).
const SUPPORTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const IMAGE_EXT_MIME = { jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", gif: "image/gif", webp: "image/webp" };

/**
 * Best-effort media type for an AI-send block. PDFs are always application/pdf.
 * For images we trust an explicit image/* type, else infer from the filename
 * extension. We deliberately do NOT default an unknown image to JPEG — sending
 * HEIC/TIFF bytes mislabeled as JPEG would 400 the request — so an unknown image
 * stays unknown and gets rejected by aiSendIssue (F1, round 3).
 */
export function aiMediaType(rawType, name, kind) {
  if (kind === "pdf") return "application/pdf";
  const t = String(rawType || "").toLowerCase();
  if (t.startsWith("image/")) return t;
  const ext = String(name || "").toLowerCase().split(".").pop();
  return IMAGE_EXT_MIME[ext] || ""; // unknown → "" → rejected, never assumed JPEG
}
// Per-file caps for AI send (F1). The durable attachment cap (25 MB) is for
// Storage; base64 inflates ~4/3 and rides inside the callable request body, so
// keep what we send to Claude well under the Firebase callable limit and
// Anthropic's per-image limit.
export const MAX_AI_IMAGE_BYTES = 5 * 1024 * 1024;   // Anthropic per-image limit
export const MAX_AI_PDF_BYTES = 16 * 1024 * 1024;    // safe under callable body limit after base64
// Aggregate budget across ALL files in one extraction (F1): per-file caps don't
// bound the total, and several files base64-expand past the callable body limit.
export const MAX_AI_TOTAL_BYTES = 18 * 1024 * 1024;  // ~24 MB base64, under the callable cap

const stripDataUrl = (dataUrl) => String(dataUrl || "").split(",")[1] || "";

// Approx decoded byte size of a base64 string (ignoring the few padding bytes).
const approxBytes = (b64) => Math.floor((String(b64 || "").length * 3) / 4);

const dataUrlType = (dataUrl) => {
  const m = /^data:([^;,]+)[;,]/.exec(String(dataUrl || ""));
  return (m && m[1]) || "";
};

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(stripDataUrl(r.result));
  r.onerror = () => reject(r.error || new Error("read failed"));
  r.readAsDataURL(blob);
});

// Only images and PDFs can be sent to Claude natively; .doc/.docx are saved but
// not read. Returns the kind, or null for an unreadable attachment type.
export const aiReadableKind = (att) =>
  att?.type === "image" ? "image" : att?.type === "pdf" ? "pdf" : null;

// True only if there are actual bytes to read. A reloaded AI-start draft keeps
// PDF/image metadata after the in-memory File is stripped (no file/dataUrl/url),
// so kind alone doesn't mean readable (F1, round 4).
export const hasReadableSource = (att) => !!(att && (att.file || att.dataUrl || att.url));

/**
 * Resolve one attachment to { id, name, kind, b64, mediaType }, or null if it
 * can't be read. Source preference: in-memory dataUrl → in-memory File → a
 * Storage download URL (fetched). Never throws — returns null on any failure so
 * one bad file can't sink the whole extraction.
 */
export async function resolveAttachmentBytes(att) {
  const kind = aiReadableKind(att);
  if (!kind) return null;
  const out = (b64, rawType) => ({ id: att.id, name: att.name, kind, b64, mediaType: aiMediaType(rawType, att.name, kind), bytes: approxBytes(b64) });
  try {
    if (att.dataUrl) {
      return out(stripDataUrl(att.dataUrl), dataUrlType(att.dataUrl));
    }
    if (att.file) {
      const b64 = await blobToBase64(att.file);
      return out(b64, att.file.type);
    }
    if (att.url) {
      const res = await fetch(att.url);
      if (!res.ok) return null;
      const blob = await res.blob();
      const b64 = await blobToBase64(blob);
      return out(b64, blob.type);
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * F15 — resolve one addendum image to an embeddable base64 dataURL so the PDF export
 * (html2pdf/html2canvas) never has to load a cross-origin Firebase Storage URL (those
 * carry no CORS headers, so html2canvas silently drops them and the image vanishes from
 * the downloaded PDF). Priority: an already-present `dataUrl` (in-memory OR hydrated from
 * the IndexedDB cache by the caller) → fetch the Storage `url` → null. Reuses
 * `resolveAttachmentBytes` (FileReader/fetch → base64) rather than re-implementing fetch.
 * `resolveBytes` is injectable for testing. Never throws — returns null on any failure.
 */
export async function resolveAddendumImageSrc(att, resolveBytes = resolveAttachmentBytes) {
  if (!att) return null;
  if (att.dataUrl) return att.dataUrl;     // already embeddable (in-memory or cache-hydrated)
  if (!att.url) return null;               // nothing to fetch
  try {
    // Addendum selection (builderHelpers.isImageAttachment) accepts both the
    // normalized "image" category and raw "image/*" MIME types (legacy rows, e.g.
    // restored quote versions predating normalization). resolveAttachmentBytes'
    // kind gate only knows "image", so normalize here — otherwise a MIME-typed
    // attachment resolves to null → falls back to the cross-origin url → gets
    // dropped from the PDF (the exact F15 bug, surviving for those rows).
    const t = String(att.type || "").toLowerCase();
    const norm = t.startsWith("image/") ? { ...att, type: "image" } : att;
    const resolved = await resolveBytes(norm);
    if (!resolved || !resolved.b64) return null;
    return `data:${resolved.mediaType || "image/jpeg"};base64,${resolved.b64}`;
  } catch {
    return null;
  }
}

/**
 * Why a resolved attachment still can't be sent to Claude — a short reason, or
 * null if it's fine to send. Guards Anthropic's supported image types (F2) and
 * the per-file size caps (F1).
 */
export function aiSendIssue(resolved) {
  if (!resolved) return null;
  if (resolved.kind === "image") {
    if (!SUPPORTED_IMAGE_TYPES.includes(String(resolved.mediaType || "").toLowerCase())) return "unsupported image type";
    if (resolved.bytes > MAX_AI_IMAGE_BYTES) return "image too large";
  } else if (resolved.kind === "pdf") {
    if (resolved.bytes > MAX_AI_PDF_BYTES) return "PDF too large";
  }
  return null;
}

/**
 * Decide, in order, which items are sent to Claude and which are skipped — the
 * single source of truth for both the AI-send path and the pre-run UI label, so
 * the row label can't promise "AI reads" for a file the aggregate budget will
 * later drop (F2, round 3). Each item: { id, name, kind, mediaType, bytes }.
 * Returns [{ id, willRead, reason }] in input order.
 */
export function planAiSend(items) {
  let total = 0;
  return (items || []).map((it) => {
    const issue = aiSendIssue(it);
    if (issue) return { id: it.id, willRead: false, reason: issue };
    if (total + (it.bytes || 0) > MAX_AI_TOTAL_BYTES) return { id: it.id, willRead: false, reason: "request size limit" };
    total += it.bytes || 0;
    return { id: it.id, willRead: true, reason: null };
  });
}
